/**
 * Validates an email address format
 * This uses a more strict validation to match the backend's email-validator
 * @param email - The email address to validate
 * @returns boolean indicating if the email is valid
 */
export const validateEmail = (email: string): boolean => {
    // This regex is more strict and matches the backend's email-validator
    // It requires:
    // - Local part (before @) must be at least 2 characters
    // - Domain part must be a valid domain with at least one period
    // - No consecutive dots
    // - No special chars at start/end of local part
    // - Domain must have at least 2 chars before TLD
    const emailRegex = /^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]{0,62}[a-zA-Z0-9]@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    
    // Additional checks
    if (!email || email.length > 254) return false;
    if (email.indexOf('@') === -1) return false;
    
    // Split and validate parts
    const parts = email.split('@');
    if (parts.length !== 2) return false;
    
    const localPart = parts[0];
    const domainPart = parts[1];
    
    // Local part must be at least 2 characters
    if (localPart.length < 2) return false;
    
    // Domain must contain at least one dot and have valid structure
    if (!domainPart.includes('.') || domainPart.startsWith('.') || domainPart.endsWith('.')) return false;
    
    return emailRegex.test(email);
};

/**
 * Validates a password against security requirements
 * @param password - The password to validate
 * @returns boolean indicating if the password meets requirements
 */
export const validatePassword = (password: string): boolean => {
    // Password must be at least 8 characters long and contain:
    // - At least one uppercase letter
    // - At least one lowercase letter
    // - At least one number
    // - At least one special character
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    return passwordRegex.test(password);
};

/**
 * Validates a full name
 * @param name - The name to validate
 * @returns boolean indicating if the name is valid
 */
export const validateFullName = (name: string): boolean => {
    // Name must be at least 2 characters long and contain only letters, spaces, and hyphens
    const nameRegex = /^[a-zA-Z\s-]{2,}$/;
    return nameRegex.test(name);
};

/**
 * Validates a phone number
 * @param phone - The phone number to validate
 * @returns boolean indicating if the phone number is valid
 */
export const validatePhone = (phone: string): boolean => {
    // Basic phone number validation (allows various formats)
    const phoneRegex = /^\+?[\d\s-()]{10,}$/;
    return phoneRegex.test(phone);
};

/**
 * Validates a company name
 * @param companyName - The company name to validate
 * @returns boolean indicating if the company name is valid
 */
export const validateCompanyName = (companyName: string): boolean => {
    // Company name must be at least 2 characters long
    return companyName.length >= 2;
}; 